/**
 * O recorte que a tela de Menções manda pra tela de Relatório.
 *
 * **Por que querystring e não estado de navegação.** O recorte precisa
 * sobreviver a um F5 e ser colável num chat — é a mesma razão pela qual
 * o filtro de upload já mora na URL (`?upload=7`). `navigate(path,
 * {state})` some no recarregamento e deixaria a pessoa numa tela de
 * relatório sem recorte nenhum, sem explicação.
 *
 * **Por que relógio de parede e não instante.** O que atravessa é
 * `YYYY-MM-DDTHH:mm` no fuso de quem clicou — exatamente o formato que
 * o `<input type="datetime-local">` do outro lado aceita. Mandar o
 * instante com offset obrigaria a tela de destino a converter de volta
 * só pra preencher o campo, e um erro nessa volta apagaria a janela do
 * formulário em silêncio. A conversão pra instante acontece uma vez só,
 * no envio, pelo `localInstant`.
 *
 * **Desde 2026-08-28 viaja TUDO** (decisão da dona): as doze dimensões
 * da tela de menções, cada uma com sua flag de incluir/excluir. Antes
 * atravessavam cinco, e as demais eram silenciosamente descartadas —
 * a pessoa filtrava por tema, clicava em gerar, e o relatório vinha
 * mais largo do que a tela mostrava.
 *
 * **O que NÃO viaja, e não é esquecimento**: `min_icena` (rede tem nota
 * NULL até o relatório calculá-la — o filtro excluiria 100% da rede) e
 * "sem classificação" (é estado transitório de triagem; congelar numa
 * foto menções que ainda vão ser classificadas contradiz o que o
 * relatório é). As duas telas avisam em vez de silenciar.
 *
 * **A valoração mínima VIAJA** (2026-08-28), e a diferença em relação
 * ao `min_icena` é o meio-escopo: ela recorta a imprensa e deixa a rede
 * passar inteira, então não há metade da foto apagada em silêncio. É a
 * primeira dimensão ESCALAR do recorte além da busca — por isso mora
 * fora do `RECORTE_DIMENSIONS`, que descreve facetas.
 *
 * **A doutrina de sanitização INVERTEU pra cinco dimensões.** Este
 * arquivo dizia "vocabulário desconhecido vira 422 depois do clique;
 * melhor descartar na entrada". Isso vale pras dimensões com
 * vocabulário FECHADO que a tela tem em memória (polaridade,
 * abordagem, fonte). Não vale pras de catálogo — marca, tema,
 * grupo-tema, porta-voz, grupo de publicador —, cujos ids só o servidor
 * conhece: aqui só se valida o FORMATO (inteiro positivo), e quem
 * recusa id inexistente é o `create_run`, com uma frase que a tela
 * mostra. Descartar em silêncio um id que a pessoa escolheu seria pior
 * que o 404.
 */

import {
  APPROACH_CHOICES,
  CRITERIA_SCHEMA_VERSION,
  LEGACY_UNCLASSIFIED,
  MEDIA_TYPE_CHOICES,
  NO_VALUE,
  SOURCE_CHOICES,
  sanitizeMinAdValue,
  type FacetCriteria,
  type MentionFilterCriteria,
} from "@/features/mentions/mention-filter-criteria";
import type {
  GenerationCriteria,
  GenerationCriteriaLabels,
  MentionScope
} from "@/shared/api/api-client";
import { polarityOptions } from "@/shared/types/polarity";
import { splitLocalInput } from "@/shared/utils/date-range";
import {
  MENTION_SEARCH_FIELDS,
  type MentionSearchField
} from "@/shared/vocabulary/mention-search-fields";

/** O mesmo teto do servidor (`core/vocabulary.py::MAX_QUERY_LENGTH`).
 *  A busca é parte da chave de um índice único; sem teto o INSERT
 *  estouraria o tamanho máximo da tupla e viraria 500. */
export const MAX_QUERY_LENGTH = 200;

/** O mesmo teto do servidor (`criteria.py::MAX_DIMENSION_VALUES`). Não
 *  é limite de banco — o índice virou hash — é sanidade de URL: um
 *  recorte com mil temas não é recorte, e a querystring estouraria a
 *  request line. */
export const MAX_DIMENSION_VALUES = 200;

const WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

/**
 * As doze dimensões, e como cada uma atravessa.
 *
 * `param` é o nome curto na querystring (repetido, um por valor);
 * `field` e `excludeField` são os nomes do payload do `POST /runs`
 * (`RunRequest`, no `web_api`). Ter as três grafias no MESMO descritor
 * é o que impede a classe de bug "dimensão que a URL carrega mas o
 * payload esquece" — quem acrescentar uma dimensão amanhã escreve uma
 * linha aqui e os quatro laços abaixo a enxergam.
 *
 * `kind: "id"` significa catálogo: o valor é um inteiro e a validação
 * aqui é só de formato (ver o cabeçalho). `"value"` significa
 * vocabulário ou texto cru, e aí `vocabulary` — quando existe — é a
 * lista fechada que a tela sabe conferir.
 *
 * **O sentinela `NO_VALUE` não viaja** — é o "sem classificação" da
 * polaridade, estado de triagem que a foto não congela. As duas
 * exceções (2026-11-01) dizem no descritor como ele viaja:
 * `noneField` (mensagem-chave e subtema: o "Sem mensagem-chave" e o "Sem
 * subtema" não são id, vão como as flags `no_key_message` e `no_theme`) e `noneTravels` (tipo de mídia: o "Não
 * informado" é um valor que o servidor aceita na própria lista).
 */
export const RECORTE_DIMENSIONS = [
  { key: "polarities", param: "polarity", field: "polarities", excludeField: "exclude_polarities", kind: "value" },
  { key: "approaches", param: "approach", field: "approaches", excludeField: "exclude_approaches", kind: "value" },
  { key: "sources", param: "source", field: "sources", excludeField: "exclude_sources", kind: "value" },
  { key: "channels", param: "channel", field: "channel_ids", excludeField: "exclude_channel_ids", kind: "id" },
  { key: "vehicles", param: "vehicle", field: "vehicle_ids", excludeField: "exclude_vehicles", kind: "id" },
  { key: "tiers", param: "tier", field: "tiers", excludeField: "exclude_tiers", kind: "value" },
  { key: "formats", param: "format", field: "formats", excludeField: "exclude_formats", kind: "value" },
  { key: "influencerTypes", param: "influencer", field: "publisher_tier_ids", excludeField: "exclude_publisher_tiers", kind: "id" },
  // Clipadora (2026-10-31): INCLUSÃO PURA. O servidor não tem
  // `exclude_clipping_services`; `inclusionOnly` faz os laços abaixo
  // nunca lerem nem escreverem a flag (a URL editada à mão não a liga).
  { key: "clippingServices", param: "clipping", field: "clipping_services", excludeField: "exclude_clipping_services", kind: "value", inclusionOnly: true },
  { key: "brands", param: "brand", field: "brand_ids", excludeField: "exclude_brands", kind: "id" },
  { key: "themes", param: "theme", field: "theme_ids", excludeField: "exclude_themes", kind: "id", noneField: "no_theme" },
  { key: "themeGroups", param: "themeGroup", field: "theme_group_ids", excludeField: "exclude_theme_groups", kind: "id" },
  { key: "spokespersons", param: "spokesperson", field: "spokesperson_ids", excludeField: "exclude_spokespersons", kind: "id" },
  { key: "publisherGroups", param: "publisherGroup", field: "publisher_group_ids", excludeField: "exclude_publisher_groups", kind: "id" },
  { key: "keyMessages", param: "keyMessage", field: "key_message_ids", excludeField: "exclude_key_messages", kind: "id", noneField: "no_key_message" },
  { key: "mediaTypes", param: "mediaType", field: "media_types", excludeField: "exclude_media_types", kind: "value", noneTravels: true },
] as const;

export type RecorteDimension = (typeof RECORTE_DIMENSIONS)[number]["key"];

type RecorteDescriptor = (typeof RECORTE_DIMENSIONS)[number];

/** O sentinela `NO_VALUE` viaja nesta dimensão? Ver o cabeçalho do
 *  descritor: só nas duas que o declaram. */
export function noValueTravels(dimension: RecorteDescriptor): boolean {
  return "noneField" in dimension || "noneTravels" in dimension;
}

/** Os valores de uma dimensão de id que são ids de verdade (sem o
 *  `NO_VALUE` do "sem", que vai por `noneField`). */
function idValues(values: string[]): string[] {
  return values.filter(value => value !== NO_VALUE);
}

export type RecorteFacet = { values: string[]; exclude: boolean };

export type ReportRecorte = {
  /** Relógio de parede local, `YYYY-MM-DDTHH:mm`. */
  from: string;
  to: string;
  facets: Record<RecorteDimension, RecorteFacet>;
  q: string;
  /** Onde `q` procura (2026-10-14). Qualificador do `q`: sem busca vale
   *  sempre `all`, a mesma regra do servidor. */
  qIn: MentionSearchField;
  /** Valoração mínima em REAIS INTEIROS, ou `null`. Fora do
   *  `RECORTE_DIMENSIONS` porque não é faceta: não tem lista nem modo
   *  excluir, então os quatro laços daquele descritor não a enxergam e
   *  ela é escrita à mão nos quatro lugares abaixo.
   *
   *  `null` e nunca `0`: no JavaScript `0` é falsy, e o piso do
   *  servidor é 1 — um zero atravessando faria a tela achar que há
   *  filtro e o relatório achar que não. */
  minAdValue: number | null;
  /** A LISTA COLADA (2026-10-03): o número da lista de links/códigos
   *  que a tela de menções guardou no servidor. Só o número viaja — os
   *  posts são milhares, e é o servidor que os expande na hora de
   *  congelar a foto. `postCount` é dica pro chip (vem da URL, não é
   *  conferido: quem manda é a lista). Fora do `RECORTE_DIMENSIONS`
   *  pelo mesmo motivo da valoração. */
  referenceSet: { id: number; postCount: number | null } | null;
  /** "Só revisadas" (`true`) / "só não revisadas" (`false`); `null` =
   *  sem filtro (2026-10-03). Escalar, escrita à mão como a valoração. */
  reviewed: boolean | null;
  /** Citação no título (2026-10-22), só imprensa: `true` = com,
   *  `false` = sem; `null` = sem filtro. Escalar, escrito à mão como a
   *  revisão; a rede passa inteira (meio-escopo, no servidor). */
  titleCitation: boolean | null;
  /** Subtema em destaque (2026-11-01): `true` = com, `false` = sem,
   *  `null` = sem filtro. Escalar, como a revisão. */
  themeHighlight: boolean | null;
  /** Post em mais de um cliente (2026-11-01): `true` = em mais de um,
   *  `false` = só neste, `null` = sem filtro. Escalar, como a revisão. */
  sharedPost: boolean | null;
  /** O filtro de UPLOAD da tela de menções (2026-10-17): ids dos
   *  uploads, no MESMO parâmetro `upload=` que a rota de menções já usa
   *  (a URL fala uma língua só). Inclusão pura; o servidor confere que
   *  são da empresa (404 senão). Fora do `RECORTE_DIMENSIONS` como a
   *  lista colada. */
  uploadIds: number[];
  /** De quem são as menções da foto (2026-10-04, relatório misto): a
   *  empresa (padrão), os concorrentes, ou todas. Escalar, escrita à
   *  mão como a revisão. */
  scope: MentionScope;
};

const emptyFacet = (): RecorteFacet => ({ values: [], exclude: false });

export const emptyRecorte = (): ReportRecorte => ({
  from: "",
  to: "",
  facets: Object.fromEntries(
    RECORTE_DIMENSIONS.map(dimension => [dimension.key, emptyFacet()])
  ) as Record<RecorteDimension, RecorteFacet>,
  q: "",
  qIn: "all",
  minAdValue: null,
  referenceSet: null,
  reviewed: null,
  titleCitation: null,
  themeHighlight: null,
  sharedPost: null,
  uploadIds: [],
  scope: "own",
});

const SCOPES: readonly MentionScope[] = ["own", "competitors", "all"];

/** As dimensões do recorte cujo catálogo é de CADA empresa (marca, tema,
 *  grupo-tema, porta-voz). Grupo de publicador é lista única do cliente
 *  e não está aqui. */
export const SCOPED_RECORTE_DIMENSIONS: readonly RecorteDimension[] = [
  "brands",
  "themes",
  "themeGroups",
  "spokespersons",
  "keyMessages",
];

/** O recorte sem as dimensões de catálogo por empresa — o que trocar o
 *  escopo de geração tem que fazer (2026-10-05). Um tema da empresa num
 *  relatório só de concorrentes é id que o servidor não acha no
 *  catálogo do escopo, e a geração tomava 404 "Tema não encontrado". */
export function withoutScopedCatalog(recorte: ReportRecorte): ReportRecorte {
  return {
    ...recorte,
    facets: {
      ...recorte.facets,
      ...Object.fromEntries(
        SCOPED_RECORTE_DIMENSIONS.map(key => [key, emptyFacet()])
      ),
    },
  };
}

/** As dimensões com vocabulário fechado que a tela tem em memória.
 *  Carregado sob demanda porque `polarityOptions()` depende de uma
 *  rota que pode não ter respondido ainda. */
function vocabularyFor(key: RecorteDimension): string[] | null {
  if (key === "polarities") return polarityOptions().map(option => option.name);
  if (key === "approaches") return APPROACH_CHOICES.map(choice => choice.value);
  if (key === "sources") return SOURCE_CHOICES.map(choice => choice.value);
  if (key === "mediaTypes") return [...MEDIA_TYPE_CHOICES.map(choice => choice.value), NO_VALUE];
  return null;
}

export function recorteToSearch(recorte: ReportRecorte): string {
  const params = new URLSearchParams();
  if (recorte.from) params.set("from", recorte.from);
  if (recorte.to) params.set("to", recorte.to);
  for (const dimension of RECORTE_DIMENSIONS) {
    const facet = recorte.facets[dimension.key];
    if (facet.values.length === 0) continue;
    for (const value of facet.values) params.append(dimension.param, value);
    // A flag só viaja quando é `true`: `exclude=false` é o padrão, e
    // escrevê-lo dobraria o tamanho da URL sem dizer nada.
    if (facet.exclude && !("inclusionOnly" in dimension)) params.set(`${dimension.param}Not`, "1");
  }
  if (recorte.q) {
    params.set("q", recorte.q);
    if (recorte.qIn !== "all") params.set("q_in", recorte.qIn);
  }
  if (recorte.minAdValue !== null) {
    params.set("minAdValue", String(recorte.minAdValue));
  }
  if (recorte.referenceSet) {
    params.set("refSet", String(recorte.referenceSet.id));
    if (recorte.referenceSet.postCount !== null) {
      params.set("refSetPosts", String(recorte.referenceSet.postCount));
    }
  }
  if (recorte.reviewed !== null) {
    params.set("reviewed", recorte.reviewed ? "1" : "0");
  }
  if (recorte.titleCitation !== null) {
    params.set("titleCitation", recorte.titleCitation ? "1" : "0");
  }
  if (recorte.themeHighlight !== null) {
    params.set("themeHighlight", recorte.themeHighlight ? "1" : "0");
  }
  if (recorte.sharedPost !== null) {
    params.set("sharedPost", recorte.sharedPost ? "1" : "0");
  }
  for (const id of recorte.uploadIds) params.append("upload", String(id));
  if (recorte.scope !== "own") params.set("scope", recorte.scope);
  return params.toString();
}

/**
 * O recorte que veio na URL, já saneado — ou `null` se não veio nenhum.
 *
 * Sanear aqui e não confiar: a URL é editável por quem quiser. Ver o
 * cabeçalho pra distinção entre o que se descarta (vocabulário
 * fechado) e o que se deixa passar pro servidor recusar (ids).
 */
export function recorteFromSearch(params: URLSearchParams): ReportRecorte | null {
  const wall = (key: string) => {
    const value = params.get(key) ?? "";
    return WALL_CLOCK.test(value) ? value : "";
  };

  const recorte = emptyRecorte();
  recorte.from = wall("from");
  recorte.to = wall("to");
  recorte.q = (params.get("q") ?? "").slice(0, MAX_QUERY_LENGTH);
  // Vocabulário fechado: valor desconhecido (ou sem busca) cai em `all`.
  const campo = params.get("q_in") as MentionSearchField | null;
  if (recorte.q && campo && MENTION_SEARCH_FIELDS.includes(campo)) {
    recorte.qIn = campo;
  }
  recorte.minAdValue = sanitizeMinAdValue(params.get("minAdValue"));
  // Número inventado passa e o servidor recusa (404, "a lista
  // expirou"), como nas dimensões de catálogo; o que se barra aqui é o
  // que nem é número.
  const revisao = params.get("reviewed");
  if (revisao === "1" || revisao === "0") recorte.reviewed = revisao === "1";
  const citacao = params.get("titleCitation");
  if (citacao === "1" || citacao === "0") recorte.titleCitation = citacao === "1";
  const destaque = params.get("themeHighlight");
  if (destaque === "1" || destaque === "0") recorte.themeHighlight = destaque === "1";
  const compartilhado = params.get("sharedPost");
  if (compartilhado === "1" || compartilhado === "0") recorte.sharedPost = compartilhado === "1";
  // Vocabulário fechado: valor desconhecido cai no padrão (a empresa).
  const escopo = params.get("scope") as MentionScope | null;
  if (escopo && SCOPES.includes(escopo)) recorte.scope = escopo;
  // Só inteiro positivo; id inventado passa e o servidor responde 404.
  recorte.uploadIds = [
    ...new Set(
      params
        .getAll("upload")
        .map(Number)
        .filter(id => Number.isInteger(id) && id > 0)
    ),
  ];
  const refSet = Number(params.get("refSet"));
  if (Number.isInteger(refSet) && refSet > 0) {
    const posts = Number(params.get("refSetPosts"));
    recorte.referenceSet = {
      id: refSet,
      postCount:
        params.get("refSetPosts") !== null &&
        Number.isInteger(posts) &&
        posts >= 0
          ? posts
          : null,
    };
  }

  for (const dimension of RECORTE_DIMENSIONS) {
    const crus = params.getAll(dimension.param);
    let values: string[];
    if (dimension.kind === "id") {
      values = crus.filter(value => {
        if (value === NO_VALUE) return noValueTravels(dimension);
        const numero = Number(value);
        return Number.isInteger(numero) && numero > 0;
      });
    } else {
      const known = vocabularyFor(dimension.key);
      // Domínio VAZIO significa "ainda não carregou" (ou a rota falhou),
      // não "nenhum valor é válido" — nesse caso o valor passa e quem
      // recusa é o servidor. Validar contra lista vazia apagaria o
      // recorte em silêncio.
      values =
        known === null || known.length === 0
          ? crus
          : crus.filter(value => known.includes(value));
    }
    // O sentinela de "sem valor" só entra onde o descritor diz — ver o
    // cabeçalho dele. Nas outras chega só por URL editada na mão.
    if (!noValueTravels(dimension)) values = values.filter(value => value !== NO_VALUE);
    if (values.length === 0) continue;
    recorte.facets[dimension.key] = {
      values: [...new Set(values)].slice(0, MAX_DIMENSION_VALUES),
      // Inclusão pura nunca é exclusão, nem com `clippingNot=1` na URL.
      exclude:
        !("inclusionOnly" in dimension) &&
        params.get(`${dimension.param}Not`) === "1",
    };
  }

  return recorteIsEmpty(recorte) ? null : recorte;
}

function recorteIsEmpty(recorte: ReportRecorte): boolean {
  if (recorte.from || recorte.to || recorte.q) return false;
  if (recorte.minAdValue !== null) return false;
  if (recorte.referenceSet !== null) return false;
  if (recorte.reviewed !== null) return false;
  if (recorte.titleCitation !== null) return false;
  if (recorte.themeHighlight !== null) return false;
  if (recorte.sharedPost !== null) return false;
  if (recorte.uploadIds.length > 0) return false;
  if (recorte.scope !== "own") return false;
  return RECORTE_DIMENSIONS.every(
    dimension => recorte.facets[dimension.key].values.length === 0
  );
}

/**
 * O payload do `POST /social-reports/runs`, montado do MESMO descritor
 * que escreveu a URL.
 *
 * A janela não sai daqui: ela é convertida pra instante uma vez só, na
 * tela, pelo `localInstant` — e é a tela que decide se manda a do
 * recorte ou a que a pessoa editou no formulário.
 */
/** Qual relatório a URL manda reabrir (`?report=` / `?draft=`).
 *
 * Mora aqui, ao lado do `recorteFromSearch`, porque faz a mesma coisa:
 * ler a URL sem confiar nela. Fora do componente porque é a única parte
 * com decisão — e assim ela tem teste sem precisar montar a tela.
 *
 * Id inventado NÃO é recusado aqui: vira 404 do servidor, que a tela já
 * sabe mostrar. O que se recusa é o que nem chega a ser um id — `NaN`,
 * zero, negativo, vazio —, porque isso viraria uma requisição a
 * `/social-reports/NaN`.
 *
 * `report` ganha de `draft` quando os dois vêm: um salvo e um rascunho
 * abertos ao mesmo tempo não existem na tela, e escolher em silêncio é
 * melhor que abrir os dois.
 */
export function openFromSearch(
  params: URLSearchParams
): { kind: "report" | "draft"; id: number } | null {
  for (const kind of ["report", "draft"] as const) {
    const cru = params.get(kind);
    if (cru === null || cru.trim() === "") continue;
    const id = Number(cru);
    if (Number.isInteger(id) && id > 0) return { kind, id };
  }
  return null;
}

export function recorteToPayload(
  recorte: ReportRecorte
): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  for (const dimension of RECORTE_DIMENSIONS) {
    const facet = recorte.facets[dimension.key];
    if (facet.values.length === 0) continue;
    if (dimension.kind === "id") {
      const ids = idValues(facet.values).map(Number);
      if (ids.length) payload[dimension.field] = ids;
      // O "sem" da mensagem-chave não é id: vai como flag própria.
      if ("noneField" in dimension && facet.values.includes(NO_VALUE)) {
        payload[dimension.noneField] = true;
      }
    } else {
      payload[dimension.field] = [...facet.values];
    }
    if (facet.exclude && !("inclusionOnly" in dimension)) {
      payload[dimension.excludeField] = true;
    }
  }
  if (recorte.q) {
    payload.q = recorte.q;
    if (recorte.qIn !== "all") payload.q_in = recorte.qIn;
  }
  if (recorte.minAdValue !== null) payload.min_ad_value = recorte.minAdValue;
  if (recorte.referenceSet !== null) {
    payload.reference_set_id = recorte.referenceSet.id;
  }
  if (recorte.reviewed !== null) payload.reviewed = recorte.reviewed;
  if (recorte.titleCitation !== null) payload.title_citation = recorte.titleCitation;
  if (recorte.themeHighlight !== null) payload.theme_highlight = recorte.themeHighlight;
  if (recorte.sharedPost !== null) payload.shared_post = recorte.sharedPost;
  if (recorte.uploadIds.length > 0) payload.upload_ids = recorte.uploadIds;
  if (recorte.scope !== "own") payload.scope = recorte.scope;
  return payload;
}

/** As dimensões que a tela de relatório não sabe editar — é a presença
 *  delas que faz aparecer o aviso de "recorte vindo de menções".
 *
 *  Marca fica de fora: desde 2026-08-28 o formulário do relatório
 *  edita marca (multi + excluir), então ela não é mais um recorte
 *  "invisível" que precisa de aviso. */
export function hasNarrowing(recorte: ReportRecorte | null): boolean {
  if (!recorte) return false;
  if (recorte.q) return true;
  // Linha própria porque o `.some` abaixo varre só o descritor de
  // facetas, e valoração mínima não está lá. Sem ela, um recorte que
  // só restringisse por valor não acenderia o aviso de "recorte vindo
  // de menções" — e o relatório sairia mais estreito que a tela sem
  // nada dizendo.
  if (recorte.minAdValue !== null) return true;
  if (recorte.referenceSet !== null) return true;
  if (recorte.reviewed !== null) return true;
  if (recorte.titleCitation !== null) return true;
  if (recorte.themeHighlight !== null) return true;
  if (recorte.sharedPost !== null) return true;
  if (recorte.uploadIds.length > 0) return true;
  return RECORTE_DIMENSIONS.some(
    dimension =>
      dimension.key !== "brands" &&
      recorte.facets[dimension.key].values.length > 0
  );
}

/** As marcas do recorte de um relatório, pelo nome CONGELADO.
 *
 *  Os rótulos vêm de `generation_criteria_labels`, nunca do catálogo
 *  vivo: renomear uma marca não pode reescrever o rótulo de um
 *  relatório salvo meses atrás. Sem rótulo (relatórios v1/v2, ou marca
 *  sumida do catálogo), cai na coluna `brand_name`, que é a projeção do
 *  caso de marca única.
 *
 *  Estava escrita duas vezes — no dashboard e na tela de relatório —,
 *  e as duas cópias truncavam em quantidades diferentes. */
export function reportBrandNames(item: {
  brand_name: string | null;
  generation_criteria?: GenerationCriteria | null;
  generation_criteria_labels?: GenerationCriteriaLabels | null;
}): string[] {
  const ids = item.generation_criteria?.brand_ids;
  const nomes = item.generation_criteria_labels?.brands;
  if (Array.isArray(ids) && ids.length > 0 && nomes) {
    const escritos = ids
      .map(id => nomes[String(id)])
      .filter((nome): nome is string => Boolean(nome));
    if (escritos.length > 0) return escritos;
  }
  return item.brand_name ? [item.brand_name] : [];
}

/** DOIS nomes e "+N" (decisão da dona, 2026-09-02).
 *
 *  O dashboard mostrava dois e a tela de relatório mostrava três, pro
 *  mesmo relatório — a lista inteira mora nos chips do recorte, então o
 *  que o rótulo precisa é caber. */
export function summarizeBrands(nomes: string[], fallback: string): string {
  if (nomes.length === 0) return fallback;
  if (nomes.length <= 2) return nomes.join(", ");
  return `${nomes.slice(0, 2).join(", ")} +${nomes.length - 2}`;
}

/** O nome provisório de uma opção de catálogo que veio sem rótulo (um
 *  recorte da URL, ou um relatório antigo): `#<id>`. O hook de filtros
 *  o troca pelo nome de hoje quando o catálogo carrega. */
export const PROVISIONAL_LABEL = /^#(\d+)$/;

export const provisionalLabel = (id: number) => `#${id}`;

/** A que grupo de `GenerationCriteriaLabels` cada dimensão de id
 *  pertence. */
const LABEL_GROUP: Partial<Record<RecorteDimension, string>> = {
  brands: "brands",
  vehicles: "vehicles",
  channels: "channels",
  influencerTypes: "publisher_tiers",
  themes: "themes",
  themeGroups: "theme_groups",
  spokespersons: "spokespersons",
  publisherGroups: "publisher_groups",
  keyMessages: "key_messages",
};

/** Canal e porte do publicador eram NOMES na criteria até 2026-11-03
 *  (`channels`, `influencer_types`); hoje são ids. Amostra gerada antes
 *  ainda traz os nomes, e a hidratação os devolve como `values` — o
 *  caminho de filtro salvo antigo: o nome vira id contra o catálogo
 *  vivo. */
const LEGACY_NAME_FIELDS: Partial<
  Record<RecorteDimension, { field: string; excludeField: string }>
> = {
  channels: { field: "channels", excludeField: "exclude_channels" },
  influencerTypes: {
    field: "influencer_types",
    excludeField: "exclude_influencer_types",
  },
};

/**
 * A criteria de um relatório (ids + rótulos congelados) no formato do
 * filtro SALVO v3 — o que o filtro compartilhado de menções entende
 * (2026-10-17, "Editar recorte").
 *
 * **Re-hidrata da CRITERIA, nunca das colunas**: a coluna `brand_id` do
 * relatório é a projeção do caso de marca única (`NULL` com duas), e
 * lê-la faria um rascunho multi-marca abrir como "empresa inteira". A
 * coluna entra só como último recurso, pros relatórios v1/v2 que não
 * têm `brand_ids` — é o `fallbackBrandId`.
 *
 * Dimensões de id viram `{label, ids:[id]}` com o nome CONGELADO no
 * relatório (`#<id>` quando ele não tem — o catálogo vivo troca depois);
 * as de valor viram `values`. A janela é relógio de parede (os dígitos),
 * não instante. `raw_post_ids` (lista colada já expandida) NÃO volta:
 * não há lista pra restaurar, e escopo/upload não são do formato salvo
 * (quem chama os lê da criteria).
 */
export function mentionCriteriaFromGeneration(
  criteria: GenerationCriteria,
  labels: GenerationCriteriaLabels | null | undefined,
  fallbackBrandId: number | null
): MentionFilterCriteria {
  const out: MentionFilterCriteria = { schema_version: CRITERIA_SCHEMA_VERSION };
  const raw = criteria as unknown as Record<string, unknown>;
  const facets: NonNullable<MentionFilterCriteria["facets"]> = {};

  for (const dimension of RECORTE_DIMENSIONS) {
    let list: unknown = raw[dimension.field];
    const legacy = LEGACY_NAME_FIELDS[dimension.key];
    if (legacy && !Array.isArray(list) && Array.isArray(raw[legacy.field])) {
      const names = (raw[legacy.field] as unknown[])
        .filter(value => typeof value === "string" && value !== NO_VALUE)
        .map(String);
      if (names.length) {
        facets[dimension.key] = {
          values: names,
          ...(raw[legacy.excludeField] === true ? { exclude: true } : {}),
        };
      }
      continue;
    }
    // O "sem" da mensagem-chave volta como o item sem id (a forma que o
    // filtro salvo guarda), com ou sem ids ao lado.
    if ("noneField" in dimension && raw[dimension.noneField] === true) {
      list = [...(Array.isArray(list) ? list : []), NO_VALUE];
    }
    if (!Array.isArray(list)) {
      // v2: polaridade e abordagem eram escalares; viram lista de um.
      if (dimension.key === "polarities" && typeof criteria.polarity === "string") {
        list = [criteria.polarity];
      } else if (
        dimension.key === "approaches" &&
        typeof criteria.approach === "string"
      ) {
        list = [criteria.approach];
      } else if (dimension.key === "brands") {
        const single = criteria.brand_id ?? fallbackBrandId;
        list = single != null ? [single] : [];
      } else {
        continue;
      }
    }
    const items = (list as unknown[]).filter(
      value =>
        value !== LEGACY_UNCLASSIFIED &&
        (value !== NO_VALUE || noValueTravels(dimension))
    );
    if (!items.length) continue;
    const exclude = raw[dimension.excludeField] === true ? { exclude: true } : {};
    if (dimension.kind === "id") {
      const group = LABEL_GROUP[dimension.key];
      const names = group
        ? (labels as Record<string, Record<string, string> | undefined> | null | undefined)?.[group]
        : undefined;
      facets[dimension.key] = {
        items: items.map(value =>
          value === NO_VALUE
            ? { label: NO_VALUE, ids: [] }
            : {
                label: names?.[String(value)] ?? provisionalLabel(Number(value)),
                ids: [Number(value)],
              }
        ),
        ...exclude,
      } satisfies FacetCriteria;
    } else {
      facets[dimension.key] = { values: items.map(String), ...exclude };
    }
  }
  if (Object.keys(facets).length) out.facets = facets;

  if (typeof criteria.window_start === "string") {
    const { day, time } = splitLocalInput(criteria.window_start);
    if (day) out.date_from = day;
    if (time) out.time_from = time;
  }
  if (typeof criteria.window_end === "string") {
    const { day, time } = splitLocalInput(criteria.window_end);
    if (day) out.date_to = day;
    if (time) out.time_to = time;
  }
  if (criteria.q) {
    out.q = criteria.q;
    if (criteria.q_in && MENTION_SEARCH_FIELDS.includes(criteria.q_in)) {
      out.q_in = criteria.q_in;
    }
  }
  const minAdValue = sanitizeMinAdValue(criteria.min_ad_value);
  if (minAdValue !== null) out.min_ad_value = minAdValue;
  if (typeof criteria.reviewed === "boolean") out.reviewed = criteria.reviewed;
  if (typeof criteria.title_citation === "boolean") {
    out.title_citation = criteria.title_citation;
  }
  if (typeof criteria.theme_highlight === "boolean") {
    out.theme_highlight = criteria.theme_highlight;
  }
  if (typeof criteria.shared_post === "boolean") out.shared_post = criteria.shared_post;
  return out;
}

/**
 * O recorte vindo da URL (`recorteFromSearch`) no formato do filtro
 * salvo — a mesma conversão de `mentionCriteriaFromGeneration`, só que
 * sem rótulos: as opções de catálogo chegam como `#<id>` e ganham o
 * nome de hoje quando o catálogo carrega. Escopo, upload e lista colada
 * não fazem parte do formato salvo; quem chama os lê do recorte.
 */
export function mentionCriteriaFromRecorte(
  recorte: ReportRecorte
): MentionFilterCriteria {
  const out: MentionFilterCriteria = { schema_version: CRITERIA_SCHEMA_VERSION };
  const facets: NonNullable<MentionFilterCriteria["facets"]> = {};
  for (const dimension of RECORTE_DIMENSIONS) {
    const facet = recorte.facets[dimension.key];
    if (!facet.values.length) continue;
    const exclude = facet.exclude ? { exclude: true } : {};
    facets[dimension.key] =
      dimension.kind === "id"
        ? {
            items: facet.values.map(value =>
              value === NO_VALUE
                ? { label: NO_VALUE, ids: [] }
                : { label: provisionalLabel(Number(value)), ids: [Number(value)] }
            ),
            ...exclude,
          }
        : { values: [...facet.values], ...exclude };
  }
  if (Object.keys(facets).length) out.facets = facets;
  if (recorte.from) {
    const { day, time } = splitLocalInput(recorte.from);
    out.date_from = day;
    if (time) out.time_from = time;
  }
  if (recorte.to) {
    const { day, time } = splitLocalInput(recorte.to);
    out.date_to = day;
    if (time) out.time_to = time;
  }
  if (recorte.q) {
    out.q = recorte.q;
    if (recorte.qIn !== "all") out.q_in = recorte.qIn;
  }
  if (recorte.minAdValue !== null) out.min_ad_value = recorte.minAdValue;
  if (recorte.reviewed !== null) out.reviewed = recorte.reviewed;
  if (recorte.titleCitation !== null) out.title_citation = recorte.titleCitation;
  if (recorte.themeHighlight !== null) out.theme_highlight = recorte.themeHighlight;
  if (recorte.sharedPost !== null) out.shared_post = recorte.sharedPost;
  return out;
}

/** `?gerar=1`: a pessoa clicou em "Fechar amostra" na tela de Menções e
 *  quer a amostra FECHADA, não o formulário (feedback da Kath,
 *  2026-10-26: "abre a página de relatórios, mas não fecha a amostra;
 *  você tem que clicar de novo"). A página gera sozinha quando o recorte
 *  já tem janela; sem janela, cai no formulário como antes. A rota tira o
 *  parâmetro da URL ao chegar, senão o F5 geraria de novo. */
export const AUTO_GENERATE_PARAM = "gerar";

export function withAutoGenerate(search: string): string {
  const params = new URLSearchParams(search);
  params.set(AUTO_GENERATE_PARAM, "1");
  return params.toString();
}

export function autoGenerateFromSearch(params: URLSearchParams): boolean {
  return params.get(AUTO_GENERATE_PARAM) === "1";
}
